import { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { useToast } from "@/hooks/useToast";
import { getApiClient, getAppBridge } from "@/api/client";
import { redirectToUrl } from "@/utils/redirect";
import { getShopId } from "@/utils/shopId";
import { billingApi } from "@/api/billing.api";
import { toErrorMessage } from "@/utils/errorMessage";
import { getAddOns } from "@/utils/planLadder";
import { PLAN_PRICES, PLAN_PRODUCT_QUOTAS } from "@/utils/planUtils";

interface QuotaCapModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Soft-cap modal shown when the monthly AR product quota is reached.
 * Shows plan-specific copy and add-on/upgrade options.
 */
export function QuotaCapModal({ isOpen, onClose }: QuotaCapModalProps) {
  const { plan } = useBilling();
  const { showError } = useToast();
  const [purchasingPack, setPurchasingPack] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(false);

  if (!isOpen) return null;

  const planCode = plan?.plan?.code;
  const maxProducts = plan?.usage?.max_assets_per_month ?? 0;

  // Server decides pack availability; the fallback is "not available", which
  // is also exactly the free-tier rule (§11.2: free-tier shops are never
  // offered packs — their only paths are upgrade or stay).
  const packAvailable = getAddOns(plan).extra_products.available;
  const packPrice = getAddOns(plan).extra_products.price;
  const packCredits = getAddOns(plan).extra_products.credits;

  const handleAddExtraProducts = async () => {
    setPurchasingPack(true);
    try {
      const client = getApiClient();
      const shopId = getShopId();
      const url = shopId
        ? `/api/billing/extra-pack/products?shop_id=${shopId}`
        : '/api/billing/extra-pack/products';
      const response = await client.post<{ checkout_url: string }>(url);
      const { checkout_url } = response.data ?? {};
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, "Failed to create checkout. Please try again."));
      setPurchasingPack(false);
    }
  };

  const handleUpgradePlan = async (targetPlan: string) => {
    setUpgradingPlan(true);
    try {
      const { checkout_url } = await billingApi.createCheckoutSession(targetPlan);
      if (!checkout_url || typeof checkout_url !== 'string') {
        throw new Error('Checkout URL missing from server response');
      }
      redirectToUrl(checkout_url, getAppBridge());
    } catch (error: unknown) {
      showError(toErrorMessage(error, "Failed to create checkout. Please try again."));
      setUpgradingPlan(false);
    }
  };

  const isLaunch = planCode === 'launch';

  // Next rung up. Quotas/prices here are marketing fallbacks — the merchant's
  // own numbers come from the server; these describe the plan they don't have
  // yet, so they must match the §11.1 table.
  const upgradeTarget = isLaunch
    ? {
        code: 'growth',
        shortName: 'Growth',
        offerName: 'The AR Sales Engine',
        blurb:
          `${PLAN_PRODUCT_QUOTAS.growth} products/month, 5 Premium Generations included, ` +
          'and a priority render queue.',
        monthlyPrice: PLAN_PRICES.growth.monthly,
        perProduct: (PLAN_PRICES.growth.monthly / PLAN_PRODUCT_QUOTAS.growth).toFixed(2),
      }
    : {
        code: 'scale',
        shortName: 'Scale',
        offerName: 'The Full-Catalog Programme',
        blurb:
          // No bonus claims beyond what the plan actually delivers. The
          // "white-glove bulk import" that used to be sold here is NOT BUILT
          // (descoped 08-06, MONETIZATION_STRATEGY_HORMOZI.md §9 J).
          `${PLAN_PRODUCT_QUOTAS.scale} products/month, unlimited Premium Generations, ` +
          'and the highest render queue priority we offer.',
        monthlyPrice: PLAN_PRICES.scale.monthly,
        perProduct: (PLAN_PRICES.scale.monthly / PLAN_PRODUCT_QUOTAS.scale).toFixed(2),
      };

  const packPerProduct =
    packCredits > 0 ? (packPrice / packCredits).toFixed(2) : packPrice.toFixed(2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-3xl border border-white/80 bg-white p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-4">
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            Monthly quota reached
          </span>
          <h2 className="mt-3 text-xl font-semibold text-slate-900">
            {isLaunch
              ? `You've used all ${maxProducts} AR products included in Launch.`
              : `You've used all ${maxProducts} AR products included in Growth.`}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Keep going with:
          </p>
        </div>

        <div className="space-y-3">
          {/* Upgrade plan — ALWAYS rendered first (§11.2 presentation rule:
              the subscription is the option we want chosen, so it leads and
              the one-time pack follows, framed as worse per-unit). */}
          <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50 p-4">
            <p className="text-sm font-semibold text-indigo-900">
              Upgrade to {upgradeTarget.shortName} — {upgradeTarget.offerName}
            </p>
            <p className="text-xs text-indigo-700 mt-1">
              {upgradeTarget.blurb} ${upgradeTarget.monthlyPrice}/mo — that&apos;s
              ${upgradeTarget.perProduct} per product.
            </p>
            <button
              onClick={() => handleUpgradePlan(upgradeTarget.code)}
              disabled={upgradingPlan}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
            >
              {upgradingPlan ? "Redirecting..." : `Upgrade to ${upgradeTarget.shortName}`}
              {!upgradingPlan && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>

          {/* Extra Product Pack — second, and only for shops the server says
              can actually buy one. Free-tier shops never see it. */}
          {packAvailable && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    +{packCredits} extra products
                  </p>
                  <p className="text-xs text-slate-500">
                    One-time add-on — ${packPerProduct} per product, no plan change
                  </p>
                </div>
                <span className="text-sm font-bold text-slate-900">${packPrice}</span>
              </div>
              <button
                onClick={handleAddExtraProducts}
                disabled={purchasingPack}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
              >
                {purchasingPack ? "Redirecting..." : `Add ${packCredits} more products`}
                {!purchasingPack && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
